import type { Command } from "commander";
import { asPaneId, type WindowId } from "../ids.js";
import { type Mux, tmux } from "../mux.js";
import { openStore, type Store } from "../store.js";
import type { TmuxServer } from "../types.js";
import { RENDER_PRIORITY, type RenderState, renderState } from "../view.js";

/**
 * Does any OTHER pane in this window still want attention?
 *
 * The badge is a WINDOW option while "the user looked" is true of one pane, so a
 * window holding an agent and a shell must keep its badge when you focus the
 * shell.
 *
 * Fails safe by keeping the badge when tmux or the store cannot answer: a badge
 * wrongly kept is recoverable by focusing the pane, one wrongly cleared loses
 * the signal.
 *
 * Exported because WRITING the badge needs the same recomputation as clearing
 * it: `notify` used to paint its own kind unconditionally, so a `done` on one
 * pane replaced the `crashed` glyph of another agent in the same window. It
 * stays here, beside its only other caller, rather than moving to view.ts --
 * that module is pure and knows nothing of `Mux` or `Store`, and giving it a
 * projection that needs both would invert the layering.
 */
export function windowBadge(
  window: WindowId,
  mux: Mux,
  store: Store,
  server: TmuxServer = { kind: "default" },
): RenderState | null {
  const panes = new Set(mux.panesInWindow(window, server));
  const states = store
    .localPanes()
    .filter(
      (pane) =>
        pane.server.kind === server.kind &&
        (pane.server.kind === "default" ||
          ("value" in server && pane.server.value === server.value)) &&
        panes.has(pane.pane),
    )
    .map((pane) =>
      renderState({
        activity: pane.agent?.activity ?? null,
        attention: pane.attention,
      }),
    );
  return RENDER_PRIORITY.find((state) => state !== "idle" && states.includes(state)) ?? null;
}

/**
 * Acknowledge every attention request on one pane, and clear its window badge.
 *
 * The whole write path. Nothing here must refuse to clear anything, because
 * attention is all focus can address: `acknowledgePane` is one `DELETE FROM
 * attention WHERE pane = ?` and cannot touch an agent's activity, identity or
 * owner metadata, so a focus hook has nothing to overwrite a running agent with.
 *
 * Best effort, silent and total: this runs inside the tmux server.
 */
export function clearPane(raw: string, mux: Mux = tmux): void {
  let store: Store | undefined;
  try {
    if (!raw) return;
    // argv is the boundary: the pane id arrives as a bare string from the hook.
    const pane = asPaneId(raw);
    // The badge is a tmux option, not murmur state, so resolving it needs no
    // murmur knowledge -- and a pane murmur has never seen can still carry an
    // orphan badge nothing else will clear.
    const current = mux.currentWindow();
    const server = current?.server ?? ({ kind: "default" } as const);
    const window = current?.pane === pane ? current.window : mux.windowForPane(pane, server);
    const location = current?.pane === pane ? current : window ? { server, pane } : null;

    try {
      store = openStore();
      if (location) store.acknowledgePane(location);
    } catch {
      // No database, or an unwritable one. Nothing was read, so there is no
      // evidence the request was satisfied -- and clearing on no evidence is the
      // one direction that loses information.
    }

    // Both guards, for the same reason, stated at the top of this file: keep the
    // badge when tmux OR the store cannot answer.
    //
    // The store guard used to be a ternary passing `null`, which erased the
    // badge without murmur ever reading the attention it reported -- the exact
    // opposite of the rule. A locked or transiently unwritable state.db plus one
    // focus event wiped the crashed/blocked glyph off a window whose attention
    // row was still in the database, and nothing repaints it until the agent's
    // next event. For a crashed agent that is never.
    //
    // A pane murmur has never seen still clears, because there `openStore`
    // succeeds and `windowBadge` returns null on its own evidence.
    if (!window || !store) return;
    // @agent_state is a derived window-scoped projection, so it is recomputed
    // after the delete: blindly clearing the option made a live running agent
    // display as idle though its agent row was untouched.
    try {
      mux.setWindowBadge(window, windowBadge(window, mux, store, server), server);
    } catch {
      // Unreadable projection: leave the badge. Stale is recoverable, erasing a
      // real signal is not.
    }
  } catch {
    // Focus hooks run inside the tmux server: they must always be silent and
    // total.
  } finally {
    try {
      store?.close();
    } catch {
      // Silent and total.
    }
  }
}

export function registerClear(program: Command): void {
  program
    .command("clear")
    .description("Acknowledge attention for a pane")
    .option("--pane <pane-id>", "focused tmux pane id")
    .action((options: { pane?: string }) => clearPane(options.pane ?? ""));
}
